import { useEffect } from "react";
import type { QuotationLineItem } from "@/mocks/quotationsData";
import { computeSubtotal, computeTaxTotal, computeTotal, companyInfo as defaultCompanyInfo } from "@/mocks/quotationsData";

type Props = {
  open: boolean;
  onClose: () => void;
  quoteNo: string;
  date: string;
  validUntil: string;
  leadName: string;
  leadPhone: string;
  leadEmail: string;
  leadCompany: string;
  items: QuotationLineItem[];
  discount: number;
  terms: string;
  agent: string;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstin?: string;
  };
};

export function FullPreviewModal({
  open,
  onClose,
  quoteNo,
  date,
  validUntil,
  leadName,
  leadPhone,
  leadEmail,
  leadCompany,
  items,
  discount,
  terms,
  agent,
  companyInfo = defaultCompanyInfo
}: Props) {
  const company = { ...defaultCompanyInfo, ...companyInfo };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const subtotal = computeSubtotal(items);
  const taxTotal = computeTaxTotal(items);
  const total = computeTotal(items, discount);

  const formatDate = (d: string) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatCurrency = (val: number) => "₹" + Number(val || 0).toLocaleString("en-IN");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto z-10">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer z-20"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Print content */}
        <div className="p-10">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <div className="w-12 h-12 rounded-lg bg-indigo-600 flex items-center justify-center mb-2">
                <span className="text-white text-sm font-bold">
                  {company.name.slice(0, 2).toUpperCase()}
                </span>
              </div>
              <h2 className="text-lg font-bold text-gray-900">{company.name}</h2>
              <p className="text-sm text-gray-500">{company.address}</p>
              <p className="text-sm text-gray-500">{company.phone}</p>
              <p className="text-sm text-gray-500">{company.email}</p>
              <p className="text-xs text-gray-400 mt-1">GSTIN: {company.gstin}</p>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wide">QUOTATION</h1>
              <p className="text-lg font-semibold text-indigo-600 mt-1">{quoteNo}</p>
              <div className="mt-3 space-y-0.5">
                <p className="text-sm text-gray-600">Date: <span className="font-medium text-gray-900">{formatDate(date)}</span></p>
                <p className="text-sm text-gray-600">Valid Until: <span className="font-medium text-gray-900">{formatDate(validUntil)}</span></p>
              </div>
            </div>
          </div>

          {/* To */}
          <div className="border-t border-b border-gray-200 py-4 mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Bill To</p>
            <p className="text-base font-semibold text-gray-900">{leadName || "—"}</p>
            {leadCompany && <p className="text-sm text-gray-600">{leadCompany}</p>}
            {leadPhone && <p className="text-sm text-gray-600">{leadPhone}</p>}
            {leadEmail && <p className="text-sm text-gray-600">{leadEmail}</p>}
          </div>

          {/* Items table */}
          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="border-b-2 border-gray-200 text-xs uppercase text-gray-500 tracking-wider">
                <th className="text-left py-2.5 pr-3 font-semibold">#</th>
                <th className="text-left py-2.5 pr-3 font-semibold">Description</th>
                <th className="text-center py-2.5 px-3 font-semibold">Qty</th>
                <th className="text-left py-2.5 px-3 font-semibold">Unit</th>
                <th className="text-right py-2.5 px-3 font-semibold">Rate</th>
                <th className="text-center py-2.5 px-3 font-semibold">Tax</th>
                <th className="text-right py-2.5 pl-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item, idx) => {
                const amt = (Number(item.qty) || 0) * (Number(item.rate) || 0) * (1 + (Number(item.taxPercent) || 0) / 100);
                return (
                  <tr key={item.id}>
                    <td className="py-2.5 pr-3 text-gray-400">{idx + 1}</td>
                    <td className="py-2.5 pr-3 font-medium text-gray-900">{item.description || "—"}</td>
                    <td className="py-2.5 px-3 text-center text-gray-700">{item.qty}</td>
                    <td className="py-2.5 px-3 text-gray-700">{item.unit}</td>
                    <td className="py-2.5 px-3 text-right text-gray-700">{formatCurrency(item.rate)}</td>
                    <td className="py-2.5 px-3 text-center text-gray-700">{item.taxPercent || 0}%</td>
                    <td className="py-2.5 pl-3 text-right font-medium text-gray-900">{formatCurrency(amt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-64 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="font-medium text-red-500">-{formatCurrency(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="font-medium text-gray-900">{formatCurrency(taxTotal)}</span>
              </div>
              <hr className="border-gray-200" />
              <div className="flex justify-between">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-indigo-600">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Terms */}
          {terms && (
            <div className="border-t border-gray-200 pt-4 mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Terms &amp; Conditions</h3>
              <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{terms}</div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 flex justify-between items-end">
            <div>
              <p className="text-sm font-semibold text-gray-900">Prepared by</p>
              <p className="text-sm text-gray-600">{agent}</p>
              <p className="text-xs text-gray-400 mt-1">This is a computer-generated quotation</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Authorized Signatory</p>
              <div className="mt-3 border-t border-gray-300 pt-1">
                <p className="text-xs text-gray-500">{company.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}